<!--
Molecule for displaying an appointment in a grid.
If clicked, go to the new session view.
-->
<template>
	<router-link
		:to="{ name: 'newSession', query: { appointmentId: appointment.id } }"
		:class="[
			isCanceled ? 'text-secondary-400 line-through' : '',
			event?.id || campaignManager?.id
				? 'pointer-events-none cursor-not-allowed'
				: '',
		]"
	>
		<div class="ml-3 flex flex-row gap-3">
			<!-- Status -->
			<AppointmentCloudSyncIcon :current-state="currentSyncState" />

			<!-- Submission error message -->
			<div
				v-if="hasSubmissionError"
				class="text-xs text-red-500"
			>
				<DocumentTextIcon class="h-5 w-5" />
			</div>

			<!-- Name -->
			<div
				v-if="lead?.firstname"
				class="text-sm font-bold"
				:class="[isCanceled ? 'text-secondary-400' : 'text-sky-700']"
			>
				{{ lead?.firstname }}
				{{ lead?.lastname }}
			</div>
			<div
				v-else
				class="text-secondary-400 text-sm font-bold italic"
			>
				{{ eventName }}
			</div>
		</div>

		<!-- Time -->
		<div :class="[!lead?.id ? 'text-secondary-400 italic' : '']">
			{{ startAt }} &ndash; {{ endAt }}
		</div>

		<!--Funeral Home Name-->

		<div v-if="lead?.funeralHome?.data.shortName">
			{{ lead?.funeralHome?.data.shortName }}
		</div>
		<div
			v-else
			class="text-secondary-400 italic"
		>
			{{ appointment?.funeralHome?.data.shortName }}
		</div>

		<!--Lead Source-->
		<div v-if="lead?.id">{{ leadSourceName }}</div>
		<div
			v-else
			class="text-secondary-400 italic"
		>
			{{ eventType }}
		</div>

		<!-- Result button -->
		<div
			v-if="isCanceled"
			data-test-id="result-text"
		>
			<div class="mt-1 text-right text-xs text-red-400 uppercase">
				{{ t('upcomingAppointments.canceled') }}
			</div>
		</div>
		<div
			v-else
			data-test-id="result-button"
			class="flex items-center justify-self-end"
		>
			<ButtonSmall
				v-if="lead?.id && appointment.resultId"
				success
			>
				{{ t('upcomingAppointments.resulted') }}
			</ButtonSmall>
			<a
				v-else-if="lead?.id"
				:href="resultHref"
				@click.stop
			>
				<ButtonSmall>
					{{ t('upcomingAppointments.result') }}
				</ButtonSmall>
			</a>
			<PrintOptions
				v-if="!event?.id && !campaignManager?.id"
				:appointment="appointment"
				:session="session"
			/>
		</div>
	</router-link>
</template>

<script setup lang="ts">
import type { CounselorAppointment } from '@/apiTypes'
import { Session } from '@/classes/Session'
import ButtonSmall from '@/components/forms/ButtonSmall.vue'
import AppointmentCloudSyncIcon from '@/components/home/AppointmentCloudSyncIcon.vue'
import PrintOptions from '@/components/home/PrintOptions.vue'
import { EventType } from '@/enums/EventType'
import { ObjectState } from '@/enums/ObjectState'
import { useCloudSyncStatusCalculation } from '@/mixins/cloudSyncStatusCalculation'
import { Feature, useFeatureService } from '@/services/featureService'
import { makeWebPortalUrl } from '@/services/webPortal'
import { useSessionStore } from '@/stores/session'
import { useSubmissionStore } from '@/stores/submission'
import { DocumentTextIcon } from '@heroicons/vue/24/solid'
import { DateTime } from 'luxon'
import { computed, ref, type PropType } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const sessionStore = useSessionStore()
const submissionStore = useSubmissionStore()
const isUsingSfIntegration = ref(
	useFeatureService().isEnabled(Feature.SalesforceIntegration)
)

const props = defineProps({
	appointment: {
		type: Object as PropType<CounselorAppointment>,
		required: true,
	},
})

/**
 * Computes the name of the event associated with the appointment.
 * If the appointment has a campaign manager, that name is returned.
 * Otherwise, the location name from the event is returned.
 */
const eventName = computed(() => {
	// Check if there's a campaign manager and return their name if it exists
	if (props.appointment.campaignManager?.data?.name) {
		return props.appointment.campaignManager.data?.name
	}
	// Otherwise, return the location name from the event
	return (
		props.appointment.event?.data?.locationName ??
		props.appointment?.subject ??
		''
	)
})

/**
 * Computes the type of event associated with the appointment.
 * If there is a campaign manager, the event type is "Discovery".
 * Otherwise, the event type is determined by the cepEventTypeId of the associated event
 */
const eventType = computed(() => {
	if (isUsingSfIntegration.value) {
		return props.appointment.event?.data?.cepEventType
	}

	if (props.appointment.campaignManager) {
		return t('home.eventType.discovery')
	}

	if (
		props.appointment.event?.data?.cepEventTypeId === EventType.TRADITIONAL_CEP
	) {
		return t('home.eventType.cep')
	}
	return t('home.eventType.acep')
})

/** The event associated with the appointment */
const event = computed(() => props.appointment.event?.data)

/** The campaign manager associated with the appointment */
const campaignManager = computed(() => props.appointment.campaignManager?.data)

/** The lead associated with the appointment */
const lead = computed(() => props.appointment.lead?.data)

/** The lead source associated with the appointment */
const leadSourceName = computed(() => {
	if (!lead.value) return null

	return lead.value.leadSourceId
		? t(`leadSources.${lead.value.leadSourceId}`)
		: (lead.value.leadSource ?? null)
})

/** The human-readable start time. Includes "AM" if it starts in the morning and ends in the evening. */
const startAt = computed(() => {
	// Format like 1:00 PM
	let time = DateTime.fromISO(props.appointment.startAt).toLocaleString(
		DateTime.TIME_SIMPLE
	)

	// Strip out the AM/PM if it matches the endAt
	if (
		time.substring(time.length - 2) ===
		endAt.value.substring(endAt.value.length - 2)
	) {
		time = time.substring(0, time.length - 3)
	}

	return time
})

/** The human-readable end time */
const endAt = computed(() =>
	DateTime.fromISO(props.appointment.endAt).toLocaleString(DateTime.TIME_SIMPLE)
)

/** Get the presentation session associated with the appointment */
const session = computed<Session | undefined>(() =>
	sessionStore.getSessionForAppointment(props.appointment.id)
)

const { currentSyncState } = useCloudSyncStatusCalculation(session.value)

// Check whether any of the plans has the status of PurchaseAPIFailed
const hasSubmissionError = computed(() => {
	const sessionStore = useSessionStore()
	return sessionStore.activePlans?.some((plan) =>
		submissionStore.planHoldersWithFailures.has(plan.id)
	)
})

/** URL for resulting the appointment */
const resultHref = computed(() => {
	if (!lead.value) {
		return undefined
	}
	return isUsingSfIntegration.value
		? makeWebPortalUrl(`lead-details/${props.appointment.opportunityId}`)
		: makeWebPortalUrl(`lead-details/${lead.value.id}`)
})

/** Check if the appointment has been canceled */
const isCanceled = computed(
	() => props.appointment.objectStateId === ObjectState.Canceled
)
</script>
